import React, { useEffect, useRef } from "react";
import styles from "./LandingPage.module.css";
import { Button, Card, CardMedia } from "@mui/material";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

gsap.registerPlugin(ScrollTrigger);

const LandingPage = () => {
  const pageUrl = "https://www.remini.com/";
  const ogImage = "https://www.remini.com/og-images/remini-ai-tools-og.jpg";

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Remini",
    url: pageUrl,
    logo: "https://www.remini.com/logo.png",
    sameAs: [
      "https://facebook.com/ReminiAI",
      "https://twitter.com/ReminiAI",
      "https://instagram.com/ReminiAI",
    ],
    priceRange: "Free",
    description:
      "Remini offers instant AI solutions for complex image editing tasks - background removal, photo enhancement, face editing & more in one click",
    image: ogImage,
    address: {
      "@type": "PostalAddress",
      addressCountry: "USA",
    },
    contactPoint: {
      "@type": "ContactPoint",
      email: "support@remini.com",
      contactType: "customer support",
      url: pageUrl,
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      bestRating: "5",
      ratingCount: "8500",
    },
    founders: [
      {
        "@type": "Person",
        name: "John Doe",
      },
    ],
    foundingDate: "2020",
    makesOffer: [
      {
        "@type": "Offer",
        name: "AI Background Remover",
        description: "Remove backgrounds instantly with AI",
      },
      {
        "@type": "Offer",
        name: "AI Image Enhancer",
        description: "Transform low-quality photos to HD",
      },
    ],
  };
  const leftCardRef = useRef(null);
  const rightCardRef = useRef(null);
  const containerRef = useRef(null);
  const titleRef = useRef(null);
  const searchSectionRef = useRef(null);
  const buttonRefs = useRef([]);
  const gradientRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Gradient Background Animation
    gsap.to(gradientRef.current, {
      backgroundPosition: "50% 100%",
      ease: "none",
      scrollTrigger: {
        trigger: containerRef.current,
        start: "top top",
        end: "bottom bottom",
        scrub: true,
      },
    });

    // Animation timelines
    const setupAnimations = () => {
      // Left Card Timeline
      const leftTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1.5,
        },
      });

      leftTimeline
        .fromTo(
          leftCardRef.current,
          { x: "-100%", opacity: 0, rotate: 15, scale: 0.8 },
          { x: "0%", opacity: 1, rotate: 10, scale: 1 }
        )
        .to(leftCardRef.current, {
          x: "-120%",
          opacity: 0,
          rotate: -25,
          scale: 0.8,
        });

      // Right Card Timeline
      const rightTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 1.5,
        },
      });

      rightTimeline
        .fromTo(
          rightCardRef.current,
          { x: "100%", opacity: 0, rotate: -15, scale: 0.8 },
          { x: "0%", opacity: 1, rotate: -10, scale: 1 }
        )
        .to(rightCardRef.current, {
          x: "120%",
          opacity: 0,
          rotate: 25,
          scale: 0.8,
        });

      // Title Animation
      gsap.fromTo(
        titleRef.current,
        { y: -50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top 80%",
          },
        }
      );

      // Search Bar Animation
      gsap.fromTo(
        searchSectionRef.current,
        { scale: 0.8, opacity: 0 },
        {
          scale: 1,
          opacity: 1,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top 75%",
          },
        }
      );

      // Buttons Animation
      gsap.fromTo(
        buttonRefs.current,
        { y: 50, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          ease: "power2.out",
          stagger: 0.2,
          scrollTrigger: {
            trigger: containerRef.current,
            start: "top 70%",
          },
        }
      );
    };

    setupAnimations();

    // Handle window resize
    const handleResize = () => {
      ScrollTrigger.refresh();
      setupAnimations();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const features = [
    {
      name: "Dashboard",
      link: "/dashboard",
    },
    {
      name: "Tools",
      link: "/dashboard",
    },
    {
      name: "Try Remini AI",
      link: "/dashboard",
    },
    {
      name: "Safety",
      link: "/safety",
    },
  ];

  const handleButtonClick = (index, link) => {
    // Update button states
    buttonRefs.current.forEach((btn, i) => {
      if (btn) btn.classList.remove(styles.activeButton);
    });
    if (buttonRefs.current[index]) {
      buttonRefs.current[index].classList.add(styles.activeButton);
    }
    // Navigate to selected page
    navigate(link);
  };
  return (
    <div ref={containerRef} className={styles.container}>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>
          Remini - Free AI Photo Editor | Instant Image Enhancement Tools
        </title>
        <meta
          name="description"
          content="Solve complex image editing in 1 second with Remini's AI tools. Remove backgrounds, enhance quality, colorize photos, and edit images instantly - 100% free online solution."
        />
        <meta
          name="keywords"
          content="AI photo editor, free image enhancer, instant background remover, AI face editor, photo colorizer, image upscaler, AI photo tools, online photo editing"
        />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta
          property="og:title"
          content="Remini - Free AI Photo Editor | Instant Image Enhancement Tools"
        />
        <meta
          property="og:description"
          content="Solve complex image editing in 1 second with Remini's AI tools. Remove backgrounds, enhance quality, colorize photos, and edit images instantly - 100% free online solution."
        />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@ReminiAI" />
        <meta name="twitter:creator" content="@ReminiAI" />
        <meta
          name="twitter:title"
          content="Remini - Free AI Photo Editor | Instant Image Enhancement Tools"
        />
        <meta
          name="twitter:description"
          content="Solve complex image editing in 1 second with Remini's AI tools. Remove backgrounds, enhance quality, colorize photos, and edit images instantly - 100% free online solution."
        />
        <meta name="twitter:image" content={ogImage} />

        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>

        {/* Language & Basic Meta */}
        <html lang="en" />
        <meta name="robots" content="index, follow" />
        <meta name="revisit-after" content="7 days" />
        <meta name="author" content="Remini" />
      </Helmet>
      <div ref={gradientRef} className={styles.gradientBackground} />

      <h1 ref={titleRef} className={styles.title}>
        AI Remini
      </h1>

      <Card ref={leftCardRef} className={`${styles.card} ${styles.leftCard}`}>
        <div className={styles.glassBg} />
        <CardMedia
          component="img"
          image="https://img.freepik.com/premium-photo/plain-background_847512-1896.jpg"
          alt="Creative Art"
          className={styles.media}
        />
      </Card>

      <Card ref={rightCardRef} className={`${styles.card} ${styles.rightCard}`}>
        <div className={styles.glassBg} />
        <video
          autoPlay
          muted
          loop
          playsInline
          src="https://leonardo-cdn.b-cdn.net/wp-content/uploads/2024/05/cat-space-med-1.mp4"
          alt="Creative Art"
          className={styles.media}
        />
      </Card>

      <div ref={searchSectionRef} className={styles.searchSection}>
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder="Do you have an idea? Write it down"
            className={styles.searchInput}
          />
          <button className={styles.generateButton}>
            <span>Generate Now</span>
            <div className={styles.buttonHover} />
            <div className={styles.buttonGlow} />
          </button>
        </div>
      </div>

      <div className={styles.buttonContainer}>
        {features.map((feature, index) => (
          <Button
            key={index}
            ref={(el) => (buttonRefs.current[index] = el)}
            className={
              feature.name === "Safety" ? styles.greenButton : styles.button
            }
            onClick={() => handleButtonClick(index, feature.link)}
          >
            {feature.name}
          </Button>
        ))}
      </div>
    </div>
  );
};

export default LandingPage;
